import { AfterViewInit, Component, DestroyRef, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MfgappsApiService } from '../../../services/mfgapps-api.service';
import { MfgappsEventService } from '../../../services/mfgapps-event.service';
import { FeedbackVersion, SupportHubRequest, SupportHubUserLookup, VersionTicket } from '../support-hub.models';
import { SupportHubStore } from '../support-hub.store';
import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker';
import { QuillEditorComponent, QuillModule, QuillModules } from 'ngx-quill';

@Component({
  selector: 'app-support-hub-release',
  standalone: true,
  imports: [FormsModule, MatProgressSpinnerModule, DialogModule, DatePickerModule, QuillModule],
  templateUrl: './support-hub-release.component.html',
  styleUrl: './support-hub-release.component.scss'
})
export class SupportHubReleaseComponent implements AfterViewInit, OnInit  {

  @ViewChild('editor', { static: true }) editorComponent!: QuillEditorComponent;

  private readonly destroyRef = inject(DestroyRef);
  readonly ticketCountOptions = [10, 25, 50, 100]
  readonly maxVisiblePageButtons = 5;

  quillModules: QuillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ]
  };



  ngAfterViewInit() {
    this.editorComponent.onEditorCreated.subscribe((quill: any) => {
      // 1. Forzar una lista al iniciar
      const length = quill.getLength();
      
      quill.formatLine(0, length, 'list', 'bullet'); // 'bullet' o 'ordered'

      quill.on('editor-change', (eventName: string) => {
        if (eventName === 'text-change' && quill.getText().trim() === '') {
          // Evitar bucles aplicando la lista solo cuando esté vacío
          quill.formatLine(0, quill.getLength, 'list', 'bullet');
        }
      });
    });
  }


  summary: string = '';
  versionData: FeedbackVersion | null = null;
  isSummaryPopupVisible = false;
  isCreateReleaseDialogVisible = false;
  newReleaseName = '';
  newReleaseDescription = '';
  newReleaseUseCaseId: number | null = null;
  newReleaseTargetDate: Date | null = null;
  readonly minReleaseTargetDate = new Date(new Date().setHours(0, 0, 0, 0));
  currentRole: string = SupportHubStore.ROLE_USER;
  selectedUseCaseId: number | null = null;
  selectedTicketCount = 10;
  selectedPipelineVersionId: number | null = null;
  selectedAssignVersionId: number | null = null;
  selectedTicketIds = new Set<number>();
  isStageFlowExpanded = true;

  versions: FeedbackVersion[] = [];
  releaseFlowStages: string[] = [];
  tickets: SupportHubRequest[] = [];
  validationUsers: SupportHubUserLookup[] = [];
  teamAssignees: SupportHubUserLookup[] = [];
  loadingValidationUsers = false;
  companyList: any[] = [];
  ticketPage = 1;
  ticketTotalCount = 0;
  ticketTotalPages = 1;
  private readonly markedForDeleteTicketIds = new Set<number>();
  private readonly newlyAssignedTicketIds = new Set<number>();
  private initialTicketState = new Map<number, any>();
  private hasInitialTicketState = false;
  showRequiredFieldHighlights = false;

  selectedTicketPopupId: number | null = null;
  selectedTicketPopupData: SupportHubRequest | null = null;

  loader = { isLoading: false, message: '' };

  constructor(
    public readonly store: SupportHubStore,
    private readonly apiService: MfgappsApiService,
    private readonly eventService: MfgappsEventService
  ) { }

  ngOnInit(): void {
    void this.initialize();
  }

  private async initialize(): Promise<void> {
    this.currentRole = this.store.currentUserRoleValue;
    this.store.currentUserRoleSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(role => {
      this.currentRole = role;
    });
    await this.store.loadLookup();
    this.companyList = await this.eventService.getCorporateHierarchy();
    this.store.selectedUseCaseIdSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(useCaseId => {
      void this.handleUseCaseSelection(useCaseId);
    });
    this.releaseFlowStages = this.store.versionStatusOptions.map(status => status.name);
  }

  private async handleUseCaseSelection(useCaseId: number | null | undefined): Promise<void> {
    const nextUseCaseId = useCaseId ?? this.store.getDefaultUseCaseId();
    if (!nextUseCaseId || nextUseCaseId === this.selectedUseCaseId) {
      return;
    }
    this.selectedUseCaseId = nextUseCaseId;
    await this.onUseCaseChange();
  }

  get isAdminRole(): boolean {
    return this.store.isAdminRoleName(this.currentRole);
  }

  openCreateReleaseDialog(): void {
    this.newReleaseName = '';
    this.newReleaseDescription = '';
    this.newReleaseUseCaseId = null;
    this.newReleaseTargetDate = null;
    this.isCreateReleaseDialogVisible = true;
  }

  closeCreateReleaseDialog(): void {
    this.isCreateReleaseDialogVisible = false;
    this.newReleaseName = '';
    this.newReleaseDescription = '';
    this.newReleaseUseCaseId = null;
    this.newReleaseTargetDate = null;
  }

  get createReleaseUseCaseOptions() {
    const allowedUseCases = new Set(['EDC', 'CCP', 'GEMS']);
    return this.store.useCaseOptions.filter(useCase => allowedUseCases.has(useCase.name.trim().toUpperCase()));
  }

  get canSubmitCreateRelease(): boolean {
    return !this.loader.isLoading
      && !!this.newReleaseName.trim()
      && !!this.newReleaseDescription.trim()
      && this.newReleaseUseCaseId !== null
      && this.newReleaseTargetDate !== null
      && this.newReleaseTargetDate.getTime() >= this.minReleaseTargetDate.getTime();
  }

  private formatTargetDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  submitCreateRelease(): void {
    if (!this.canSubmitCreateRelease) {
      return;
    }
    this.eventService.showSnackBar('Release creation is not connected to the backend yet.', 'error');
  }

  get priorityOptions(): Array<{ id: number; name: string }> {
    return this.store.priorityOptions.map(item => ({ id: item.id, name: item.name }));
  }

  get ticketStatusOptions(): Array<{ id: number; name: string }> {
    return this.store.statusOptions.map(item => ({ id: item.id, name: item.name }));
  }

  get developerOptions(): SupportHubUserLookup[] {
    return this.teamAssignees;
  }

  get validatorOptions(): SupportHubUserLookup[] {
    return this.validationUsers;
  }

  get assignableVersions(): FeedbackVersion[] {
    const assignableStatusIds = this.store.getAssignableVersionStatusIds();

    return this.versions.filter(version => {
      const currentStatus = this.getCurrentStageStatus(version);
      return !!currentStatus && assignableStatusIds.has(currentStatus.statusId);
    });
  }

  get newTickets(): SupportHubRequest[] {
    const assignedIds = new Set<number>();
    this.versions.forEach(version => version.tickets?.forEach(t => assignedIds.add(t.feedback_id)));
    return this.tickets.filter(ticket => !assignedIds.has(ticket.feedback_id ?? 0));
  }

  get selectedNewTicketCount(): number {
    return this.newTickets.filter(ticket => this.selectedTicketIds.has(ticket.feedback_id ?? 0)).length;
  }

  get canAssignSelectedTickets(): boolean {
    return this.selectedNewTicketCount > 0 && !!this.selectedAssignVersionId;
  }

  get hasPendingReleaseChanges(): boolean {
    return this.hasInitialTicketState && this.hasReleaseChanges();
  }

  get visiblePageNumbers(): number[] {
    if (this.ticketTotalPages <= 1) {
      return [1];
    }
    const halfWindow = Math.floor(this.maxVisiblePageButtons / 2);
    let start = Math.max(1, this.ticketPage - halfWindow);
    const end = Math.min(this.ticketTotalPages, start + this.maxVisiblePageButtons - 1);

    if (end - start + 1 < this.maxVisiblePageButtons) {
      start = Math.max(1, end - this.maxVisiblePageButtons + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }

  getAssignedTickets(versionId: number): VersionTicket[] {
    return this.versions.find(v => v.version_id === versionId)?.tickets ?? [];
  }

  private getCurrentStageStatus(version: FeedbackVersion) {
    return this.store.versionStatusOptions.find(status => status.statusId === version.version_status_id);
  }

  getVersionStageName(version: FeedbackVersion): string {
    return version.version_status ?? this.getCurrentStageStatus(version)?.name ?? '';
  }

  getVersionColor(version: FeedbackVersion): string {
    return this.getCurrentStageStatus(version)?.colorCode ?? '';
  }

  private getNextStageStatus(version: FeedbackVersion) {
    const currentStatus = this.getCurrentStageStatus(version);
    if (!currentStatus) {
      return undefined;
    }
    return this.store.versionStatusOptions.find(s => s.sortOrder === currentStatus.sortOrder + 1);
  }

  getNextActionLabel(version: FeedbackVersion): string {
    const nextStage = this.getNextStageStatus(version);
    return nextStage ? `Set as ${nextStage.name}` : '';
  }

  getNextActionColor(version: FeedbackVersion) {
    return this.getNextStageStatus(version)?.colorCode;
  }

  canPromote(version: FeedbackVersion): boolean {
    return !!this.getNextStageStatus(version);
  }

  async confirmDeleteVersion(event: Event, version: FeedbackVersion): Promise<void> {
    event.stopPropagation();
    const message = `Do you want to delete release "${version.version_name}" ?`;
    if (!await this.eventService.confirmDialog(message)) {
      return;
    }

    this.setLoader('Deleting release...');
    try {
      const currentStageStatus = this.getCurrentStageStatus(version);
      await this.apiService.deleteFeedbackVersion({
        version_id: version.version_id,
        usecase_id: version.usecase_id ?? this.selectedUseCaseId ?? 0,
        version_name: version.version_name,
        version_status_id: version.version_status_id ?? currentStageStatus?.statusId ?? 0,
        version_status_name: version.version_status ?? currentStageStatus?.name ?? ''
      });

      await Promise.all([this.loadVersions(), this.loadTickets()]);
      this.selectedPipelineVersionId = this.versions[0]?.version_id ?? null;
      this.selectedAssignVersionId = this.assignableVersions[0]?.version_id ?? null;
      this.selectedTicketIds.clear();
      this.markedForDeleteTicketIds.clear();
      this.newlyAssignedTicketIds.clear();
      this.captureInitialTicketState();
      this.hasInitialTicketState = true;
      this.showRequiredFieldHighlights = false;

      this.eventService.showSnackBar(`Release "${version.version_name}" deleted successfully.`, 'success');
    }
    catch (error: any) {
      this.eventService.showSnackBar(error?.message ?? 'Failed to delete release. Please try again.', 'error');
    }
    finally {
      this.resetLoader();
    }
  }

  ticketStatusClass(statusId?: number | null): string {
    if (statusId === this.store.statusIds.NEW) {
      return 'new';
    }
    if (statusId === this.store.statusIds.IN_PROGRESS) {
      return 'in-progress';
    }
    if (statusId === this.store.statusIds.UAT_VALIDATING) {
      return 'validation';
    }
    if (statusId === this.store.statusIds.COMPLETE) {
      return 'complete';
    }
    return '';
  }

  getStatusLabel(statusId: number | null | undefined): string {
    return this.store.statusOptions.find(s => s.id === statusId)?.name ?? '';
  }

  private getPromotionContext(version: FeedbackVersion): {
    currentStage: string;
    nextStage: string;
    toVersionStatusId: number;
  } | null {
    const currentStageStatus = this.getCurrentStageStatus(version);
    const nextStageStatus = this.getNextStageStatus(version);

    if (!currentStageStatus || !nextStageStatus) {
      return null;
    }

    const currentStage = currentStageStatus.name;
    const nextStage = nextStageStatus.name;

    // Moving a release from Future to Development is allowed even if another release is already in Development.
    const isFutureToDevelopment = currentStage === 'Future' && nextStage === 'Development';

    // If there is another stage after next, block duplicate release in the next stage.
    if (!isFutureToDevelopment && this.store.versionStatusOptions.some(s => s.sortOrder === nextStageStatus.sortOrder + 1)) {
      const existingRelease = this.versions.find(v => this.getVersionStageName(v) === nextStage);
      if (existingRelease) {
        const promotionBlockedMessage = `Cannot promote to ${nextStage}: Release "${existingRelease.version_name}" is already in ${nextStage} stage.`;
        this.eventService.showSnackBar(promotionBlockedMessage, 'error');
        return null;
      }
    }

    return {
      currentStage,
      nextStage,
      toVersionStatusId: nextStageStatus.statusId
    };
  }

  onVersionStatusClick(version: FeedbackVersion): void {
    if (this.loader.isLoading) {
      return;
    }

    const promotionContext = this.getPromotionContext(version);
    if (!promotionContext) {
      return;
    }

    this.versionData = version;
    this.summary = '';
    this.isSummaryPopupVisible = true;
  }

  async addSummary(): Promise<void> {
      if (!this.versionData) {
        this.eventService.showSnackBar('No release selected for status change.', 'error');
        return;
      }

      const targetVersion = this.versionData;
      const promotionContext = this.getPromotionContext(targetVersion);
      if (!promotionContext) {
        return;
      }

      const summaryArray = this.summary
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean);

      if (summaryArray.length === 0) {
        this.eventService.showSnackBar('Summary cannot be empty.', 'error');
        return;
      }

      const message = `Do you want to save version change summary and promote release "${targetVersion.version_name}" from "${promotionContext.currentStage}" to "${promotionContext.nextStage}" ?`;
      if (!await this.eventService.confirmDialog(message)) {
        return;
      }

      this.loader.isLoading = true;

      try {
        const useCaseId = this.selectedUseCaseId ?? 0;
        await this.apiService.updateVersionStatus(targetVersion.version_id, useCaseId, promotionContext.toVersionStatusId);

        const payload = {
          version_id: targetVersion.version_id,
          summary: summaryArray
        };
        await this.apiService.addSummary(payload);

        await this.loadVersions();
        this.selectedPipelineVersionId = this.versions.some(v => v.version_id === this.selectedPipelineVersionId)
          ? this.selectedPipelineVersionId
          : (this.versions[0]?.version_id ?? null);
        this.selectedAssignVersionId = this.assignableVersions[0]?.version_id ?? null;
        this.markedForDeleteTicketIds.clear();
        this.newlyAssignedTicketIds.clear();
        this.captureInitialTicketState();
        this.hasInitialTicketState = true;
        this.showRequiredFieldHighlights = false;
        this.eventService.showSnackBar(`Release "${targetVersion.version_name}" promoted to ${promotionContext.nextStage} successfully.`, 'success');

        this.isSummaryPopupVisible = false;
        this.summary = '';
        this.versionData = null;
      }
      catch (error: any) {
        this.eventService.showSnackBar(error?.message ?? 'Failed to save summary and promote release.', 'error');
      }
      finally {
        this.loader.isLoading = false;
      }
  }
  
  async confirmPromoteVersion(version: FeedbackVersion): Promise<void> {
    const promotionContext = this.getPromotionContext(version);
    if (!promotionContext) {
      return;
    }

    const message = `Do you want to Promote release "${version.version_name}" from "${promotionContext.currentStage}" to "${promotionContext.nextStage}" ?`;
    if (await this.eventService.confirmDialog(message)) {
      try {
        const useCaseId = this.selectedUseCaseId ?? 0;
        await this.apiService.updateVersionStatus(version.version_id, useCaseId, promotionContext.toVersionStatusId);
        await this.loadVersions();
        this.selectedPipelineVersionId = this.versions.some(v => v.version_id === this.selectedPipelineVersionId)
          ? this.selectedPipelineVersionId
          : (this.versions[0]?.version_id ?? null);
        this.selectedAssignVersionId = this.assignableVersions[0]?.version_id ?? null;
        this.markedForDeleteTicketIds.clear();
        this.newlyAssignedTicketIds.clear();
        this.captureInitialTicketState();
        this.hasInitialTicketState = true;
        this.showRequiredFieldHighlights = false;
        this.eventService.showSnackBar(`Release "${version.version_name}" promoted to ${promotionContext.nextStage} successfully.`, 'success');
      }
      catch (error) {
        this.eventService.showSnackBar(`Failed to promote release: ${error}`, 'error');
      }
    }
  }

  async onUseCaseChange(): Promise<void> {
    this.setLoader('Loading release pipeline...');
    try {
      this.hasInitialTicketState = false;
      this.ticketPage = 1;
      await this.loadValidationUsers();
      await Promise.all([this.loadVersions(), this.loadTickets()]);
      this.selectedPipelineVersionId = this.versions[0]?.version_id ?? null;
      this.selectedAssignVersionId = this.assignableVersions[0]?.version_id ?? null;
      this.selectedTicketIds.clear();
      this.isStageFlowExpanded = false;
      this.markedForDeleteTicketIds.clear();
      this.newlyAssignedTicketIds.clear();
      this.captureInitialTicketState();
      this.hasInitialTicketState = true;
      this.showRequiredFieldHighlights = false;
    }
    finally {
      this.resetLoader();
    }
  }

  async onTicketCountChange(): Promise<void> {
    this.setLoader('Loading tickets...');
    try {
      this.ticketPage = 1;
      await this.loadTickets();
      this.selectedTicketIds.clear();
    }
    finally {
      this.resetLoader();
    }
  }

  async goToTicketPage(page: number): Promise<void> {
    if (!this.selectedUseCaseId || this.loader.isLoading || page < 1 || page > this.ticketTotalPages) {
      return;
    }
    this.setLoader('Loading tickets...');
    try {
      this.ticketPage = page;
      await this.loadTickets();
      this.selectedTicketIds.clear();
    }
    finally {
      this.resetLoader();
    }
  }

  async prevTicketPage(): Promise<void> {
    if (this.ticketPage <= 1) {
      return;
    }
    await this.goToTicketPage(this.ticketPage - 1);
  }

  async nextTicketPage(): Promise<void> {
    if (this.ticketPage >= this.ticketTotalPages) {
      return;
    }
    await this.goToTicketPage(this.ticketPage + 1);
  }

  selectPipelineVersion(versionId: number): void {
    this.selectedPipelineVersionId = this.selectedPipelineVersionId === versionId ? null : versionId;
  }

  isTicketSelected(ticket: SupportHubRequest): boolean {
    return this.selectedTicketIds.has(ticket.feedback_id ?? 0);
  }

  toggleTicketSelection(ticket: SupportHubRequest, checked: boolean): void {
    const ticketId = ticket.feedback_id ?? 0;
    if (!ticketId) {
      return;
    }
    if (checked) {
      this.selectedTicketIds.add(ticketId);
      return;
    }
    this.selectedTicketIds.delete(ticketId);
  }

  assignSelectedTickets(): void {
    if (!this.canAssignSelectedTickets || !this.selectedAssignVersionId) {
      return;
    }
    const assignVersionId = this.selectedAssignVersionId;
    const targetVersion = this.versions.find(v => v.version_id === assignVersionId);
    if (!targetVersion) {
      return;
    }
    const toAssign = this.tickets.filter(ticket => this.selectedTicketIds.has(ticket.feedback_id ?? 0));

    toAssign.forEach(ticket => {
      const vt: VersionTicket = {
        usecase_id: ticket.usecase_id,
        feedback_id: ticket.feedback_id ?? 0,
        corporate_id: ticket.corporate_id ?? 0,
        company_id: ticket.company_id ?? 0,
        plant_id: ticket.plant_id ?? 0,
        shop_id: ticket.shop_id ?? 0,
        workcategory_id: ticket.workcategory_id,
        workcategorytype_id: ticket.workcategorytype_id,
        application_id: ticket.application_id,
        rank_id: ticket.rank_id,
        priority_id: ticket.priority_id,
        status_id: ticket.status_id,
        title: ticket.title,
        description: ticket.description,
        creation_date: ticket.creation_date,
        requested_user: ticket.requested_user,
        assigned_user: ticket.assigned_user,
        assigned_team: ticket.assigned_team,
        contractor_user: ticket.contractor_user,
        validated_user: ticket.validated_user,
        requested_user_name: ticket.requested_user_name || this.getUserNameById(ticket.requested_user),
        assigned_user_name: ticket.assigned_user_name || this.getUserNameById(ticket.assigned_user),
        validated_user_name: ticket.validated_user_name || this.getUserNameById(ticket.validated_user)
      };
      targetVersion.tickets.push(vt);
      if (vt.feedback_id) {
        this.markedForDeleteTicketIds.delete(vt.feedback_id);
        this.newlyAssignedTicketIds.add(vt.feedback_id);
      }
    });
    this.tickets = this.tickets.filter(ticket => !this.selectedTicketIds.has(ticket.feedback_id ?? 0));
    this.selectedPipelineVersionId = assignVersionId;
    this.selectedTicketIds.clear();
  }

  unassignTicket(ticket: VersionTicket): void {
    const version = this.versions.find(v => v.tickets.some(t => t.feedback_id === ticket.feedback_id));
    if (!version) {
      return;
    }
    if (this.initialTicketState.has(ticket.feedback_id)) {
      this.markedForDeleteTicketIds.add(ticket.feedback_id);
      return;
    }
    this.newlyAssignedTicketIds.delete(ticket.feedback_id);
    version.tickets = version.tickets.filter(t => t.feedback_id !== ticket.feedback_id);
    if (this.tickets.some(t => t.feedback_id === ticket.feedback_id)) {
      return;
    }
    this.tickets.unshift({
      feedback_id: ticket.feedback_id,
      title: ticket.title,
      description: ticket.description,
      creation_date: ticket.creation_date,
      corporate_id: ticket.corporate_id,
      company_id: ticket.company_id,
      plant_id: ticket.plant_id,
      shop_id: ticket.shop_id,
      usecase_id: ticket.usecase_id,
      workcategory_id: ticket.workcategory_id,
      workcategorytype_id: ticket.workcategorytype_id,
      application_id: ticket.application_id,
      rank_id: ticket.rank_id,
      priority_id: ticket.priority_id,
      status_id: ticket.status_id,
      user_id: 0,
      requested_user: ticket.requested_user,
      requested_user_name: ticket.requested_user_name,
      assigned_user: ticket.assigned_user,
      assigned_user_name: ticket.assigned_user_name,
      assigned_team: ticket.assigned_team,
      contractor_user: ticket.contractor_user,
      validated_user: ticket.validated_user,
      validated_user_name: ticket.validated_user_name,
      is_liked: false,
      activityLog: [],
      is_following: false
    });
  }

  undoUnassignTicket(ticket: VersionTicket): void {
    this.markedForDeleteTicketIds.delete(ticket.feedback_id);
  }

  isTicketMarkedForDelete(feedbackId: number): boolean {
    return this.markedForDeleteTicketIds.has(feedbackId);
  }

  isTicketNewlyAssigned(feedbackId: number): boolean {
    return this.newlyAssignedTicketIds.has(feedbackId);
  }

  hasMissingRequiredFields(ticket: VersionTicket): boolean {
    return !this.markedForDeleteTicketIds.has(ticket.feedback_id)
      && (ticket.assigned_user == null || ticket.priority_id == null || ticket.status_id == null);
  }

  async saveReleaseChanges(): Promise<void> {
    if (this.loader.isLoading || !this.hasPendingReleaseChanges) {
      return;
    }
    const missingFieldTickets = this.getTicketsMissingRequiredFields();
    if (missingFieldTickets.length > 0) {
      this.showRequiredFieldHighlights = true;
      const missingTicketsString = missingFieldTickets.map(t => `Version ${t.versionName} - Ticket #${t.feedback_id} - ${t.title}`).join('\n');
      this.eventService.showSnackBar(
        `Developer and Priority are required for the following assigned tickets before saving changes:\n\n${missingTicketsString}`,
        'error'
      );
      return;
    }
    if (!await this.eventService.confirmDialog('Do you want to save all release changes ?')) {
      return;
    }
    this.setLoader('Saving release changes...');
    try {
      const payload = this.buildBulkUpdatePayload();
      const response = await this.apiService.bulkUpdateFeedbackEntries(payload);
      if (response?.message && response.message !== 'Success') {
        this.eventService.showSnackBar(response?.error ?? 'Unable to save release changes. Please try again !', 'error');
        return;
      }
      await Promise.all([this.loadVersions(), this.loadTickets()]);
      this.selectedTicketIds.clear();
      this.markedForDeleteTicketIds.clear();
      this.newlyAssignedTicketIds.clear();
      this.captureInitialTicketState();
      this.hasInitialTicketState = true;
      this.showRequiredFieldHighlights = false;
      this.eventService.showSnackBar('Release changes saved successfully !', 'success');
    }
    catch (error: any) {
      this.eventService.showSnackBar(error?.message ?? 'Unable to save release changes. Please try again !', 'error');
    }
    finally {
      this.resetLoader();
    }
  }

  async resetReleaseChanges(): Promise<void> {
    if (this.loader.isLoading || !this.hasPendingReleaseChanges) {
      return;
    }


    this.setLoader('Resetting release changes...');
    const previousPipelineVersionId = this.selectedPipelineVersionId;

    try {
      await Promise.all([this.loadVersions(), this.loadTickets()]);

      this.selectedPipelineVersionId = this.versions.some(v => v.version_id === previousPipelineVersionId)
        ? previousPipelineVersionId
        : (this.versions[0]?.version_id ?? null);

      this.selectedAssignVersionId = this.assignableVersions[0]?.version_id ?? null;
      this.selectedTicketIds.clear();
      this.markedForDeleteTicketIds.clear();
      this.newlyAssignedTicketIds.clear();
      this.captureInitialTicketState();
      this.hasInitialTicketState = true;
      this.showRequiredFieldHighlights = false;

    }
    catch (error: any) {
      this.eventService.showSnackBar(error?.message ?? 'Unable to reset release changes. Please try again !', 'error');
    }
    finally {
      this.resetLoader();
    }
  }

  private buildBulkUpdatePayload(): any[] {
    const currentState = this.buildCurrentTicketState();
    const payload: any[] = [];
    for (const [feedbackId, current] of currentState.entries()) {
      const initial = this.initialTicketState.get(feedbackId);
      if (this.isTicketStateChanged(initial, current)) {
        payload.push(current);
      }
    }
    return payload;
  }

  private getTicketsMissingRequiredFields() {
    const missing: (VersionTicket & { versionName: string })[] = [];
    this.versions.forEach(version => {
      version.tickets.forEach(ticket => {
        if (this.hasMissingRequiredFields(ticket)) {
          missing.push({ ...ticket, versionName: version.version_name });
        }
      });
    });
    return missing;
  }

  private hasReleaseChanges(): boolean {
    const currentState = this.buildCurrentTicketState();
    if (currentState.size !== this.initialTicketState.size) {
      return true;
    }
    for (const [feedbackId, current] of currentState.entries()) {
      const initial = this.initialTicketState.get(feedbackId);
      if (this.isTicketStateChanged(initial, current)) {
        return true;
      }
    }
    return false;
  }

  private captureInitialTicketState(): void {
    this.initialTicketState = this.buildCurrentTicketState();
  }

  private buildCurrentTicketState(): Map<number, any> {
    const state = new Map<number, any>();
    this.versions.forEach(version => {
      version.tickets.forEach(ticket => {
        state.set(ticket.feedback_id, {
          feedback_id: ticket.feedback_id,
          assigned_user: ticket.assigned_user,
          contractor_user: ticket.assigned_user,
          priority_id: ticket.priority_id,
          status_id: ticket.status_id,
          version_id: this.markedForDeleteTicketIds.has(ticket.feedback_id) ? null : version.version_id,
          validated_user: ticket.validated_user
        });
      });
    });
    return state;
  }

  private isTicketStateChanged(initial: any, current: any): boolean {
    if (!initial || !current) {
      return initial || current;
    }
    return initial.version_id !== current.version_id
      || initial.assigned_user !== current.assigned_user
      || initial.contractor_user !== current.contractor_user
      || initial.priority_id !== current.priority_id
      || initial.status_id !== current.status_id
      || initial.validated_user !== current.validated_user;
  }

  private async loadVersions(): Promise<void> {
    const response = await this.apiService.getFeedbackVersions(this.selectedUseCaseId ?? 0, "feedback");
    const completedStatusIds = this.store.getCompletedVersionStatusIds();
    this.versions = Array.isArray(response?.result)
      ? response.result
        .filter((v: any) => !completedStatusIds.has(v.version_status_id))
        .map((v: any) => ({
        ...v,
        tickets: Array.isArray(v.tickets)
          ? v.tickets
            .map((ticket: VersionTicket) => {
              ticket.requested_user_name = ticket.requested_user_name || this.getUserNameById(ticket.requested_user);
              ticket.assigned_user_name = ticket.assigned_user_name || this.getUserNameById(ticket.assigned_user);
              ticket.validated_user_name = ticket.validated_user_name || this.getUserNameById(ticket.validated_user);
              return ticket;
            })
          : []
      }))
      : [];
  }

  private getUserNameById(userId?: number): string {
    const matchedUser = this.validationUsers.find(user => user.user_id === userId || user.id === userId);
    return matchedUser?.user_name ?? '';
  }

  private async loadValidationUsers(): Promise<void> {
    this.loadingValidationUsers = true;
    try {
      const response = await this.apiService.getFeedbackUsers();
      this.validationUsers = Array.isArray(response?.result) ? response.result : [];

      await this.store.loadLookup();
      const developerRoleId = this.store.lookupValue.roles
        .find(role => role.role_name === SupportHubStore.ROLE_DEVELOPER && role.usecase_id === this.selectedUseCaseId)
        ?.role_id;

      this.teamAssignees = developerRoleId
        ? this.validationUsers.filter(user => user.role_id === developerRoleId)
        : [];
    }
    finally {
      this.loadingValidationUsers = false;
    }
  }

  private async loadTickets(): Promise<void> {
    const response = await this.apiService.searchFeedbackEntries({
      filters: {
        usecase_id: this.selectedUseCaseId,
        status_id: this.store.statusIds.NEW,
        version_id: null
      },
      page: this.ticketPage,
      rowsPerPage: this.selectedTicketCount
    });

    const records = Array.isArray(response?.result?.records) ? response.result.records : [];
    this.ticketPage = Number(response?.result?.page) || this.ticketPage;
    this.ticketTotalCount = Number(response?.result?.totalCount) || 0;
    const apiTotalPages = Number(response?.result?.totalPages) || 0;
    this.ticketTotalPages = Math.max(1, apiTotalPages || Math.ceil(this.ticketTotalCount / this.selectedTicketCount));
    this.tickets = records as SupportHubRequest[];
  }

  private setLoader(message: string): void {
    this.loader.isLoading = true;
    this.loader.message = message;
  }

  private resetLoader(): void {
    this.loader.isLoading = false;
    this.loader.message = '';
  }

  openTicketPopup(ticketId: number): void {
    this.selectedTicketPopupId = ticketId;
    this.selectedTicketPopupData = this.resolvePopupTicket(ticketId);
  }

  closeTicketPopup(): void {
    this.selectedTicketPopupId = null;
    this.selectedTicketPopupData = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.selectedTicketPopupData) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }
    const clickedInsidePopup = !!target.closest('.ticket-popup');
    const clickedTicketLink = !!target.closest('.ticket-link');
    if (!clickedInsidePopup && !clickedTicketLink) {
      this.closeTicketPopup();
    }
  }

  getTicketStatusLabel(ticket: SupportHubRequest | VersionTicket): string {
    return this.store.statusOptions.find(s => s.id === ticket.status_id)?.name ?? '';
  }

  getPriorityLabel(ticket: SupportHubRequest | null): string {
    const priorityId = ticket?.priority_id;
    if (!priorityId) {
      return '-';
    }
    return this.store.priorityOptions.find(p => p.id === priorityId)?.name ?? '-';
  }

  getPopupUseCaseLabel(ticket: SupportHubRequest | null): string {
    if (!ticket?.usecase_id) {
      return '-';
    }
    return this.store.getUseCaseNameById(ticket.usecase_id) || '-';
  }

  getPopupCategoryLabel(ticket: SupportHubRequest): string {
    return this.store.getWorkCategoryNameById(ticket.workcategory_id) || '-';
  }

  getPopupRequestTypeLabel(ticket: SupportHubRequest | null): string {
    if (!ticket?.usecase_id) {
      return '-';
    }
    return this.store.getRequestTypeNameByIds(ticket.usecase_id, ticket.workcategorytype_id ?? null) || '-';
  }

  getPopupApplicationLabel(ticket: SupportHubRequest | null): string {
    if (!ticket?.usecase_id) {
      return '-';
    }
    return this.store.getApplicationNameById(ticket.usecase_id, ticket.application_id ?? null) || '-';
  }

  getPopupAssignedTeamLabel(ticket: SupportHubRequest | null): string {
    return ticket?.assigned_team_name ?? '-';
  }

  private resolvePopupTicket(ticketId: number): SupportHubRequest | null {
    let ticket = this.tickets.find(ticket => ticket.feedback_id === ticketId);
    if (!ticket) {
      for (const version of this.versions) {
        const assignedTicket = version.tickets.find(ticket => ticket.feedback_id === ticketId);
        if (assignedTicket) {
          ticket = {
            feedback_id: assignedTicket.feedback_id,
            title: assignedTicket.title,
            description: assignedTicket.description,
            creation_date: assignedTicket.creation_date,
            corporate_id: assignedTicket.corporate_id,
            company_id: assignedTicket.company_id,
            plant_id: assignedTicket.plant_id,
            shop_id: assignedTicket.shop_id,
            usecase_id: assignedTicket.usecase_id,
            workcategory_id: assignedTicket.workcategory_id,
            workcategorytype_id: assignedTicket.workcategorytype_id,
            application_id: assignedTicket.application_id,
            rank_id: assignedTicket.rank_id,
            priority_id: assignedTicket.priority_id,
            status_id: assignedTicket.status_id,
            user_id: 0,
            requested_user: assignedTicket.requested_user,
            requested_user_name: assignedTicket.requested_user_name,
            assigned_user: assignedTicket.assigned_user,
            assigned_user_name: assignedTicket.assigned_user_name,
            assigned_team: assignedTicket.assigned_team,
            assigned_team_name: assignedTicket.assigned_team_name,
            contractor_user: assignedTicket.contractor_user,
            validated_user: assignedTicket.validated_user,
            validated_user_name: assignedTicket.validated_user_name,
            version: version.version_name,
            is_liked: false,
            activityLog: [],
            is_following: false
          };
        }
      }
    }
    if (ticket) {
      this.store.setCompanyPlantShopLabels(this.companyList, ticket);
    }
    return ticket ?? null;
  }

   async createNewReleaseEntry(): Promise<void> {
      if (!this.canSubmitCreateRelease) {
        return;
      }
      if (!await this.eventService.confirmDialog('Do you want to save all release changes ?')) {
        return;
      }
      const payload = {
        version_name: this.newReleaseName,
        version_description: this.newReleaseDescription,
        usecase_id: this.newReleaseUseCaseId,
        target_date: this.formatTargetDateForApi(this.newReleaseTargetDate!)
      };

      this.setLoader('Creating release...');
      try {
        const response = await this.apiService.createNewReleaseEntry(payload);
        if (response?.message && response.message !== 'Success') {
          this.eventService.showSnackBar(response?.error ?? 'Unable to create release. Please try again.', 'error');
          return;
        }

        await Promise.all([this.loadVersions(), this.loadTickets()]);
        this.selectedPipelineVersionId = this.versions[0]?.version_id ?? null;
        this.selectedAssignVersionId = this.assignableVersions[0]?.version_id ?? null;
        this.selectedTicketIds.clear();
        this.markedForDeleteTicketIds.clear();
        this.newlyAssignedTicketIds.clear();
        this.captureInitialTicketState();
        this.hasInitialTicketState = true;
        this.showRequiredFieldHighlights = false;

        this.eventService.showSnackBar('Release created successfully.', 'success');
        this.closeCreateReleaseDialog();
      }
      catch (error: any) {
        this.eventService.showSnackBar(error?.message ?? 'Unable to create release. Please try again.', 'error');
      }
      finally {
        this.resetLoader();
      }
  }
}